import { useEffect, useMemo, useState } from 'react';
import { Plus, ClipboardList, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { useAuth } from '../context/AuthContext';
import { claimsAPI, employeesAPI, selfAPI } from '../services/api';
import { filterBySearch } from '../utils/search';

const STATUS_COLORS = {
  submitted: 'bg-blue-50 text-blue-700',
  under_review: 'bg-amber-50 text-amber-700',
  query_raised: 'bg-orange-50 text-orange-700',
  approved: 'bg-green-50 text-green-700',
  partially_approved: 'bg-yellow-50 text-yellow-700',
  rejected: 'bg-red-50 text-red-700',
  settled: 'bg-emerald-50 text-emerald-700',
  withdrawn: 'bg-slate-50 text-slate-600',
};

const CLAIM_TYPES = ['cashless', 'reimbursement', 'top_up', 'corporate_buffer'];
const STATUSES = ['submitted', 'under_review', 'query_raised', 'approved', 'partially_approved', 'rejected', 'settled', 'withdrawn'];
const DOC_TYPES = ['claim_form', 'hospital_bills', 'discharge_summary', 'prescriptions', 'kyc', 'bank_details', 'other'];

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

function ClaimCard({ claim, onRefresh, isEmployee = false }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [docType, setDocType] = useState('claim_form');
  const [file, setFile] = useState(null);
  const [queryNotes, setQueryNotes] = useState('');
  const [queryResponse, setQueryResponse] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadDetail = async () => {
    const { data } = isEmployee
      ? await selfAPI.getClaim(claim.id)
      : await claimsAPI.get(claim.id);
    setDetail(data);
    setQueryNotes(data.query_notes || '');
    setQueryResponse(data.query_response || '');
  };

  const toggle = async () => {
    if (!expanded && !detail) await loadDetail();
    setExpanded(!expanded);
  };

  const updateStatus = async (status) => {
    if (isEmployee) return;
    await claimsAPI.updateStatus(claim.id, {
      status,
      query_notes: queryNotes || undefined,
      query_response: queryResponse || undefined,
    });
    onRefresh();
    if (expanded) loadDetail();
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', docType);
    const uploadFn = isEmployee ? selfAPI.uploadDocument : claimsAPI.uploadDocument;
    try {
      await uploadFn(claim.id, formData);
      setFile(null);
      await loadDetail();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-start gap-3">
        <div className="bg-primary-50 p-2 rounded-lg"><ClipboardList className="w-5 h-5 text-primary-600" /></div>
        <div className="flex-1">
          <div className="flex justify-between gap-2">
            <h3 className="font-semibold">{claim.claim_number}</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${STATUS_COLORS[claim.status]}`}>{claim.status?.replace(/_/g, ' ')}</span>
          </div>
          <p className="text-sm text-slate-500">{claim.employee_name}</p>
          <p className="text-sm mt-2">Amount: {formatCurrency(claim.claim_amount)}</p>
          <button type="button" onClick={toggle} className="text-sm text-primary-600 mt-2 flex items-center gap-1">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide details' : 'Documents & query'}
          </button>

          {expanded && (
            <div className="mt-4 space-y-4 border-t pt-4">
              {!isEmployee && (
                <div>
                  <label className="label">Update Status</label>
                  <select className="input-field text-xs" value={claim.status} onChange={(e) => updateStatus(e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              )}

              {(claim.status === 'query_raised' || queryNotes) && (
                <div className="space-y-2">
                  <div>
                    <label className="label">Query Notes (HR/Insurer)</label>
                    <textarea className="input-field text-sm" rows={2} value={queryNotes} onChange={(e) => setQueryNotes(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Query Response</label>
                    <textarea className="input-field text-sm" rows={2} value={queryResponse} onChange={(e) => setQueryResponse(e.target.value)} />
                  </div>
                  {!isEmployee && (
                    <button type="button" onClick={() => updateStatus(claim.status)} className="btn-secondary text-sm">Save Query</button>
                  )}
                </div>
              )}

              {detail?.documents?.length > 0 && (
                <ul className="text-sm space-y-1">
                  {detail.documents.map((d) => (
                    <li key={d.id} className="text-slate-600 capitalize">{d.document_type.replace(/_/g, ' ')} — {d.file_name}</li>
                  ))}
                </ul>
              )}

              <form onSubmit={handleUpload} className="space-y-2">
                <label className="label flex items-center gap-1"><Upload className="w-4 h-4" />Upload Document</label>
                <select className="input-field text-sm" value={docType} onChange={(e) => setDocType(e.target.value)}>
                  {DOC_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="input-field text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <button type="submit" disabled={!file || uploading} className="btn-primary text-sm">{uploading ? 'Uploading…' : 'Upload'}</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Claims() {
  const { user } = useAuth();
  const isEmployee = user?.role === 'employee';
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', policy_id: '', claim_type: 'reimbursement',
    claim_amount: '', hospital_name: '', admission_date: '', discharge_date: '', diagnosis: '',
    family_member_id: '',
  });
  const [coveredMembers, setCoveredMembers] = useState([]);
  const [search, setSearch] = useState('');

  const filteredClaims = useMemo(
    () => filterBySearch(claims, search, [
      'claim_number',
      'employee_name',
      'hospital_name',
      'claim_type',
      'status',
      'diagnosis',
    ]),
    [claims, search]
  );

  const load = () => (isEmployee ? selfAPI.claims() : claimsAPI.list()).then(({ data }) => setClaims(data));

  useEffect(() => {
    const tasks = [load()];
    if (!isEmployee) {
      tasks.push(employeesAPI.list().then(({ data }) => setEmployees(data)));
    } else {
      tasks.push(selfAPI.summary().then(({ data }) => {
        setCoveredMembers(data.familyMembers || []);
      }));
    }
    Promise.all(tasks).finally(() => setLoading(false));
  }, [isEmployee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmployee) {
      await selfAPI.createClaim({
        claim_type: form.claim_type,
        claim_amount: parseFloat(form.claim_amount),
        hospital_name: form.hospital_name,
        admission_date: form.admission_date,
        discharge_date: form.discharge_date,
        diagnosis: form.diagnosis,
        family_member_id: form.family_member_id ? parseInt(form.family_member_id, 10) : null,
      });
    } else {
      const emp = employees.find((x) => String(x.id) === String(form.employee_id));
      await claimsAPI.create({
        ...form,
        employee_id: parseInt(form.employee_id, 10),
        policy_id: emp?.policy_id || parseInt(form.policy_id, 10),
        claim_amount: parseFloat(form.claim_amount),
      });
    }
    setShowForm(false);
    load();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">{isEmployee ? 'My Claims' : 'Claim Management'}</h1>
          <p className="text-slate-500">{isEmployee ? 'Raise and track your medical claims' : 'M8 — cashless, reimbursement, top-up & corporate buffer claims'}</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />Raise Claim
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card grid md:grid-cols-3 gap-4">
          {!isEmployee && (
            <div>
              <label className="label">Employee *</label>
              <select className="input-field" required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                <option value="">Select</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
              </select>
            </div>
          )}
          {isEmployee && (
            <div>
              <label className="label">Patient / Covered Member *</label>
              <select className="input-field" required value={form.family_member_id} onChange={(e) => setForm({ ...form, family_member_id: e.target.value })}>
                <option value="">Select member</option>
                {coveredMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.member_name} ({m.relation?.replace(/_/g, ' ')})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="label">Claim Type *</label>
            <select className="input-field" value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })}>
              {CLAIM_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div><label className="label">Claim Amount *</label><input type="number" className="input-field" required value={form.claim_amount} onChange={(e) => setForm({ ...form, claim_amount: e.target.value })} /></div>
          <div><label className="label">Hospital</label><input className="input-field" value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} /></div>
          <div><label className="label">Admission</label><input type="date" className="input-field" value={form.admission_date} onChange={(e) => setForm({ ...form, admission_date: e.target.value })} /></div>
          <div><label className="label">Discharge</label><input type="date" className="input-field" value={form.discharge_date} onChange={(e) => setForm({ ...form, discharge_date: e.target.value })} /></div>
          <div className="md:col-span-3"><label className="label">Diagnosis</label><input className="input-field" value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>
          <div className="md:col-span-3"><button type="submit" className="btn-primary">Submit Claim</button></div>
        </form>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search claims by number, employee, hospital, status…"
        value={search}
        onChange={setSearch}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredClaims.map((claim) => (
          <ClaimCard key={claim.id} claim={claim} onRefresh={load} isEmployee={isEmployee} />
        ))}
      </div>
      {claims.length > 0 && filteredClaims.length === 0 && (
        <p className="text-center py-8 text-slate-500">No claims match your search.</p>
      )}
    </div>
  );
}
